import { rangeBetween, shuffle } from '~/utils/helpers'
import { randomIntFromInterval } from '~/bootcamp/Activities/utils/activities.utils'

/**
 * Generate equation numbers and result
 * @param min minimum number to generate numbers
 * @param max maximum number to generate numbers
 * @param isFirstDivision is division at first position
 * @param taskHistoryDividends history of dividend numbers
 * @param taskHistoryMultipliers history of multiplier numbers
 * @returns object containing object numbers and answer
 */
export function generateNumbersAndResult(
  min: number,
  max: number,
  isFirstDivision: boolean,
  taskHistoryDividends: number[],
  taskHistoryMultipliers: number[],
): { numbers: number[]; result: number } {
  const dividend = randomIntFromInterval(min, max, taskHistoryDividends)
  const divisor = generateDivisor(dividend, min)
  const multiPlier = randomIntFromInterval(min, max, taskHistoryMultipliers)
  const numbers = isFirstDivision ? [dividend, divisor, multiPlier] : [multiPlier, dividend, divisor]
  const result = Math.floor((dividend / divisor) * multiPlier)
  return {
    numbers,
    result,
  }
}

/**
 * Generate divisor number based on dividend number
 * @param dividend dividend number
 * @param min minimum possible divisor number
 * @returns divisor number
 */
function generateDivisor(dividend: number, min: number): number {
  const allNumbersInRange = rangeBetween(min, dividend + 1)
  const divisors = allNumbersInRange.filter((num) => dividend % num === 0)
  if ((min > 1 && divisors.length !== 1) || (min === 1 && divisors.length > 2)) {
    // if dividend not prime then remove dividend from divisors to avoid same dividend and divisor
    // eslint-disable-next-line fp/no-mutating-methods
    divisors.pop()
  }
  return shuffle(divisors)[0]
}

/**
 * Generates equation from numbers and result
 * @param numbers numbers to check
 * @param operator operator to check
 * @returns return the equation
 */
export function generateFromNumberAndOperators(numbers: number[], operator: string[]): string {
  return `${numbers[0]} ${operator[0]} ${numbers[1]} ${operator[1]} ${numbers[2]}`
}
